import React, { useState } from "react";
import { Users, BarChart3, ClipboardList, Zap, Clock, DollarSign, Receipt, Wallet, FileText, TrendingDown, TrendingUp, Activity, PiggyBank, Banknote, X, TriangleAlert } from "lucide-react";
import { getAccountCurrency, displayDecimalFormatted } from "../../../utils/systemSettings";
import styles from "../ResourcesReport.module.scss";

const KPI_CONFIG = [
  { key: "resources",         label: "Resources",      icon: Users },
  { key: "capacityHours",     label: "Capacity",       icon: BarChart3 },
  { key: "allocationHours",   label: "Allocation",     icon: ClipboardList },
  { key: "loggedHours",       label: "Logged",         icon: Clock },
  { key: "utilization",       label: "Utilization",    icon: Zap },
  { key: "loggedUtilization", label: "Logged Util",    icon: Activity },
  { key: "allocCost",         label: "Alloc Cost",     icon: DollarSign },
  { key: "loggedCost",        label: "Logged Cost",    icon: Wallet },
  { key: "costVariance",      label: "Cost Variance",  icon: TrendingDown },
  { key: "allocBill",         label: "Alloc Bill",     icon: Receipt },
  { key: "loggedBill",        label: "Logged Bill",    icon: FileText },
  { key: "billVariance",      label: "Bill Variance",  icon: TrendingUp },
  { key: "allocGp",           label: "Alloc GP",       icon: PiggyBank },
  { key: "loggedGp",          label: "Logged GP",      icon: Banknote },
];

export default function ReportKPICards({ consolidated, settings, columns, onHideColumn }) {
  const currency = getAccountCurrency(settings);
  const c = consolidated;
  const formatDecimal = (val) => `${displayDecimalFormatted(settings, val ?? 0)}`;
  const formatDecimalMoney = (val) => `${currency} ${displayDecimalFormatted(settings, val ?? 0)}`;

  const capacityHours = c.totalCapacityHours ?? 0;
  const loggedHours = c.totalLoggedHours ?? 0;
  const allocHours = c.totalAllocationHours ?? 0;
  const loggedCost = c.totalLoggedCost ?? 0;
  const allocCost = c.totalAllocationCost ?? 0;
  const loggedBill = c.totalLoggedBill ?? 0;
  const allocBill = c.totalAllocationBill ?? 0;
  const utilization = c.averageUtilizationPercentage ?? 0;
  const loggedUtilization = c.averageLoggedUtilizationPercentage ?? 0;
  const allocGp = c.totalAllocationGp ?? (allocBill - allocCost);
  const loggedGp = c.totalLoggedGp ?? (loggedBill - loggedCost);
  const costVariance = loggedCost - allocCost;
  const billVariance = loggedBill - allocBill;

  const visibleKeys = new Set(columns.filter((col) => col.isVisible).map((col) => col.key));

  const cards = [
    { key: "resources",         value: c.resourceCount ?? 0,                                overrun: false, tooltip: null },
    { key: "capacityHours",     value: `${formatDecimal(Number(capacityHours.toFixed(2)))}h`,               overrun: false, tooltip: null },
    { key: "allocationHours",   value: `${formatDecimal(Number(allocHours.toFixed(2)))}h`,                  overrun: allocHours > capacityHours,                                                    tooltip: allocHours > capacityHours ? `Over-allocated by ${formatDecimal(Number((allocHours - capacityHours).toFixed(2)))}h beyond capacity` : null },
    { key: "loggedHours",       value: `${formatDecimal(Number(loggedHours.toFixed(2)))}h`,                 overrun: loggedHours > allocHours,                                                       tooltip: loggedHours > allocHours ? `Hours overrun: +${formatDecimal(Number((loggedHours - allocHours).toFixed(2)))}h over allocation` : null },
    { key: "utilization",       value: `${formatDecimal(Number(utilization.toFixed(2)))}%`,                  overrun: utilization > 100,                                                              tooltip: utilization > 100 ? `Over-utilized by ${formatDecimal(Number((utilization - 100).toFixed(2)))}%` : null },
    { key: "loggedUtilization", value: `${formatDecimal(Number(loggedUtilization.toFixed(2)))}%`,            overrun: loggedUtilization > 100 || loggedUtilization > utilization,                      tooltip: loggedUtilization > 100 ? `Logged utilization exceeds 100% by ${formatDecimal(Number((loggedUtilization - 100).toFixed(2)))}%` : loggedUtilization > utilization ? `Logged utilization exceeds planned by ${formatDecimal(Number((loggedUtilization - utilization).toFixed(2)))}%` : null },
    { key: "allocCost",         value: formatDecimalMoney(allocCost),                                       overrun: false, tooltip: null },
    { key: "loggedCost",        value: formatDecimalMoney(loggedCost),                                      overrun: loggedCost > allocCost,                                                         tooltip: loggedCost > allocCost ? `Cost overrun: +${formatDecimalMoney(loggedCost - allocCost)} over allocation` : null },
    { key: "costVariance",      value: formatDecimalMoney(costVariance),                                    overrun: costVariance > 0,                                                               tooltip: costVariance > 0 ? `Cost overrun: ${formatDecimalMoney(costVariance)} over budget` : null },
    { key: "allocBill",         value: formatDecimalMoney(allocBill),                                       overrun: false, tooltip: null },
    { key: "loggedBill",        value: formatDecimalMoney(loggedBill),                                      overrun: loggedBill < allocBill,                                                         tooltip: loggedBill < allocBill ? `Bill shortfall: ${formatDecimalMoney(allocBill - loggedBill)} below allocation` : null },
    { key: "billVariance",      value: formatDecimalMoney(billVariance),                                    overrun: billVariance < 0,                                                               tooltip: billVariance < 0 ? `Bill shortfall: ${formatDecimalMoney(Math.abs(billVariance))} below target` : null },
    { key: "allocGp",           value: formatDecimalMoney(allocGp),                                         overrun: false, tooltip: null },
    { key: "loggedGp",          value: formatDecimalMoney(loggedGp),                                        overrun: false, tooltip: null },
  ];

  const visibleCards = cards.filter((card) => visibleKeys.has(card.key));

  const gridCols = visibleCards.length <= 4
    ? visibleCards.length
    : Math.ceil(visibleCards.length / 2);

  return (
    <div className={styles.kpiSection}>
      <div className={styles.kpiGrid} style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
        {visibleCards.map((card) => {
          const cfg = KPI_CONFIG.find((k) => k.key === card.key);
          const col = columns.find((c) => c.key === card.key);
          const isGreen = card.overrun === false && [
            "allocationHours", "loggedHours", "utilization", "loggedUtilization",
            "loggedCost", "costVariance", "loggedBill", "billVariance",
          ].includes(card.key);
          const isRed = card.overrun;
          const statusClass = isRed ? styles.kpiCardRed : isGreen ? styles.kpiCardGreen : "";

          // Conditional icons for variance cards
          let Icon = cfg.icon;
          if (card.key === "costVariance") Icon = costVariance > 0 ? TrendingUp : TrendingDown;
          if (card.key === "billVariance") Icon = billVariance < 0 ? TrendingDown : TrendingUp;

          return (
            <KPICard
              key={card.key}
              card={card}
              cfg={cfg}
              col={col}
              Icon={Icon}
              isGreen={isGreen}
              isRed={isRed}
              statusClass={statusClass}
              onHideColumn={onHideColumn}
            />
          );
        })}
      </div>
    </div>
  );
}

function KPICard({ card, cfg, col, Icon, isGreen, isRed, statusClass, onHideColumn }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const hasTooltip = isRed && card.tooltip;

  return (
    <div
      className={`${styles.kpiCard} ${statusClass}`}
      onMouseEnter={() => hasTooltip && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {onHideColumn && (
        <button
          type="button"
          className={styles.kpiCloseBtn}
          onClick={(e) => { e.stopPropagation(); onHideColumn(card.key); }}
          title="Hide this card"
        >
          <X size={12} />
        </button>
      )}
      {showTooltip && card.tooltip && (
        <div className={styles.kpiTooltip}>
          <TriangleAlert size={13} color="#dc2626" />
          <span>{card.tooltip}</span>
        </div>
      )}
      <div className={`${styles.kpiIconBadge} ${isRed ? styles.kpiIconBadgeRed : isGreen ? styles.kpiIconBadgeGreen : ""}`}>
        <Icon size={18} />
      </div>
      <div className={styles.kpiContent}>
        <span className={styles.kpiLabel}>{col?.customName || cfg.label}</span>
        <span className={`${styles.kpiValue} ${isRed ? styles.kpiValueRed : isGreen ? styles.kpiValueGreen : ""}`}>
          {card.value}
        </span>
      </div>
    </div>
  );
}
